import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, User2, Star, ShieldCheck, Zap, ShieldAlert } from "lucide-react";

const INSISTANCE_STYLES = {
  none:     { border: "border-[#059669]/30", bg: "bg-[#ECFDF5]", dot: "text-[#065F46]" },
  soft:     { border: "border-[#C5A880]/40", bg: "bg-[#FBF6EA]", dot: "text-[#7A5B14]" },
  moderate: { border: "border-[#F59E0B]/40", bg: "bg-[#FEF7EC]", dot: "text-[#B45309]" },
  strong:   { border: "border-[#DC2626]/40", bg: "bg-[#FEF2F2]", dot: "text-[#991B1B]" },
};

const URGENCY_LABEL = {
  fr: { low: "Autonomie", moderate: "Utile", high: "Recommandé", critical: "Fortement recommandé" },
  en: { low: "Autonomous", moderate: "Useful", high: "Recommended", critical: "Strongly advised" },
};

export default function CopilotWidget({ lang }) {
  const [data, setData] = useState(null);
  const [m3, setM3] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Run legacy next-step + doctrinal M3 recommendation in parallel
        const [r, rec] = await Promise.all([
          api.get("/copilot/next-step"),
          api.get("/diagnostic/me/recommendation").catch(() => null),
        ]);
        setData(r.data);
        setM3(rec?.data || null);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return null;

  const s = INSISTANCE_STYLES[data.insistance] || INSISTANCE_STYLES.none;
  const urgencyLabel = URGENCY_LABEL[lang]?.[data.urgency] || data.urgency;
  const isSelf = data.verdict === "self_manage";

  return (
    <div data-testid="copilot-widget"
         className={`rounded-xl border ${s.border} ${s.bg} p-6 shadow-sm altiora-transition`}>
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-white border border-[#E4E1D8] flex items-center justify-center flex-shrink-0 shadow-sm">
          <Sparkles className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
              {lang === "fr" ? "Copilote ALTIORA" : "ALTIORA Copilot"}
            </div>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest ${s.dot}`}>
              {urgencyLabel}
            </span>
          </div>
          <div className="mt-2 text-sm text-[#1F2937] leading-relaxed whitespace-pre-line">
            {data.narrative}
          </div>

          {data.why && data.why.length > 0 && !isSelf && (
            <details className="mt-3">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-[#5B5B62] hover:text-[#1F2937]">
                {lang === "fr" ? "Pourquoi cette recommandation ?" : "Why this recommendation?"}
              </summary>
              <ul className="mt-2 space-y-1.5">
                {data.why.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#5B5B62]">
                    <ShieldCheck className="h-3 w-3 mt-0.5 text-[#0B2545] flex-shrink-0" strokeWidth={2} />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {data.citations && data.citations.length > 0 && (
            <details className="mt-2" data-testid="copilot-citations">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-[#7A5B14] hover:text-[#5A4308]">
                {lang === "fr" ? "Sources ALTIORA" : "ALTIORA sources"} · {data.citations.length}
              </summary>
              <ul className="mt-2 space-y-1">
                {data.citations.map((c, i) => (
                  <li key={i} className="text-[11px] text-[#5B5B62] pl-3 border-l-2 border-[#C5A880]">
                    <span className="text-[10px] font-mono text-[#8B8B8F]">
                      {c.collection_label_fr} · v{c.version}
                    </span>
                    <div className="text-[#1F2937]">{c.title_fr}</div>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {data.suggested_pro && data.cta_visibility >= 1 && (
            <div className="mt-4 rounded-lg border border-[#E4E1D8] bg-white p-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#F1EFE9] border border-[#E4E1D8] flex items-center justify-center flex-shrink-0">
                <span className="font-display text-sm text-[#0B2545]">
                  {data.suggested_pro.first_name?.[0]}{data.suggested_pro.last_name?.[0]}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1F2937] truncate">
                  {data.suggested_pro.first_name} {data.suggested_pro.last_name}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-[#5B5B62]">
                  <span className="truncate">{data.suggested_pro.title_fr}</span>
                  <span className="inline-flex items-center gap-0.5 text-[#C5A880]">
                    <Star className="h-2.5 w-2.5 fill-[#C5A880]" strokeWidth={1.5} />
                    <span className="text-[#1F2937] font-medium">{data.suggested_pro.rating?.toFixed(1)}</span>
                  </span>
                </div>
              </div>
              <Link
                to="/app/professionals"
                data-testid="copilot-book"
                className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md altiora-transition shadow-sm ${
                  data.cta_visibility >= 3
                    ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    : data.cta_visibility >= 2
                      ? "bg-[#0B2545] hover:bg-[#123163] text-white"
                      : "border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                }`}
              >
                {lang === "fr" ? "Prendre RDV" : "Book"}
                <ArrowRight className="h-3 w-3" strokeWidth={2} />
              </Link>
            </div>
          )}

          {/* M3 · Recommandations doctrinales (quick wins + à éviter) */}
          {m3?.answer && ((m3.answer.quick_wins_fr || []).length > 0 || (m3.answer.avoid_fr || []).length > 0) && (
            <div className="mt-5 pt-4 border-t border-[#E4E1D8]/70" data-testid="copilot-m3">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7A5B14] mb-3 inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                {lang === "fr" ? "Recommandations · Doctrine M3" : "Recommendations · M3"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(m3.answer.quick_wins_fr || []).slice(0, 3).length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1.5 inline-flex items-center gap-1">
                      <Zap className="h-3 w-3" /> {lang === "fr" ? "Quick wins" : "Quick wins"}
                    </div>
                    <ul className="space-y-1.5">
                      {m3.answer.quick_wins_fr.slice(0, 3).map((w, i) => (
                        <li key={i} data-testid={`copilot-quickwin-${i}`}
                            className="flex items-start gap-2 text-xs text-[#374151] leading-relaxed">
                          <span className="text-emerald-600 mt-0.5 flex-shrink-0">▸</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(m3.answer.avoid_fr || []).length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#B45309] mb-1.5 inline-flex items-center gap-1">
                      <ShieldAlert className="h-3 w-3" /> {lang === "fr" ? "À éviter" : "Avoid"}
                    </div>
                    <ul className="space-y-1.5">
                      {m3.answer.avoid_fr.slice(0, 3).map((a, i) => (
                        <li key={i} data-testid={`copilot-avoid-${i}`}
                            className="flex items-start gap-2 text-xs text-[#374151] leading-relaxed">
                          <span className="text-[#B45309] mt-0.5 flex-shrink-0">▸</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="mt-3 text-[10px] font-mono text-[#8B8B8F]">
                {lang === "fr" ? "Confiance " : "Confidence "}
                {Math.round((m3.confidence || 0) * 100)}%
                {(m3.sources_fr || []).length > 0 && (
                  <> · {(m3.sources_fr || []).slice(0, 1).join(" · ")}</>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
